// src/hooks/useScopeTemplates.js
// Admin-managed scope-of-work templates persisted in Supabase app_settings.
// Each template follows the same stage/deliverable shape as DEFAULT_SCOPE_TEMPLATE
// so it can be passed directly to createContractScope().

import { useState, useCallback, useEffect, useRef } from 'react';
import { DEFAULT_SCOPE_TEMPLATE, INTERIOR_SECTIONS } from '../utils/contractScopeHelpers';

const SETTINGS_KEY = 'scope_templates';

// Backfill missing `section` on a template's interior deliverables list.
// Templates persisted before sectioning default to 'warm-shell' so admins
// can re-assign them in the editor.
function normaliseTemplate(t) {
  if (!t) return t;
  const items = Array.isArray(t.interiorDeliverables) ? t.interiorDeliverables : [];
  if (items.every(d => d.section)) return t;
  return {
    ...t,
    interiorDeliverables: items.map(d =>
      d.section ? d : { ...d, section: INTERIOR_SECTIONS.WARM_SHELL }
    ),
  };
}

let _nextId = Date.now();

export default function useScopeTemplates(settingsHook) {
  const [templates, setTemplates] = useState([]);
  const initialised = useRef(false);
  const saveTimer = useRef(null);

  // scope_templates is excluded from the initial settings fetch because of its
  // size. This hook is instantiated in App.js for every session, so it must NOT
  // fetch on mount — pages that show templates call ensureLoaded() instead.
  // Idempotent: useSettings dedupes concurrent calls per key.
  const loadHeavySetting = settingsHook?.loadHeavySetting;
  const ensureLoaded = useCallback(
    () => loadHeavySetting?.(SETTINGS_KEY),
    [loadHeavySetting]
  );

  // Load templates from settingsHook.appSettings once the value arrives
  useEffect(() => {
    if (!settingsHook) return;
    const raw = settingsHook.appSettings?.[SETTINGS_KEY];
    if (!raw) return;
    let parsed = raw;
    if (typeof raw === 'string') {
      try { parsed = JSON.parse(raw); } catch { return; }
    }
    if (Array.isArray(parsed)) {
      setTemplates(parsed.map(normaliseTemplate));
      initialised.current = true;
    }
  }, [settingsHook?.appSettings?.[SETTINGS_KEY]]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist to Supabase (debounced to avoid excessive writes)
  const persistToSupabase = useCallback((updatedTemplates) => {
    if (!settingsHook?.updateSetting) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      settingsHook.updateSetting(SETTINGS_KEY, updatedTemplates);
    }, 500);
  }, [settingsHook]);

  // Helper: update state and persist
  const updateAndPersist = useCallback((updater) => {
    setTemplates(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      persistToSupabase(next);
      return next;
    });
  }, [persistToSupabase]);

  // Create a new template from the BRIO default as starting point
  const createTemplate = useCallback((name) => {
    const now = new Date().toISOString();
    const tpl = {
      ...DEFAULT_SCOPE_TEMPLATE,
      id: `sow-tpl-${++_nextId}`,
      name: name || 'New SOW Template',
      status: 'draft',
      // Deep-copy template-level fields
      templateIntroduction: DEFAULT_SCOPE_TEMPLATE.templateIntroduction || '',
      interiorDeliverables: (DEFAULT_SCOPE_TEMPLATE.interiorDeliverables || []).map(d => ({
        ...d,
        section: d.section || INTERIOR_SECTIONS.WARM_SHELL,
      })),
      // Deep-copy stages so edits don't mutate the default
      stages: DEFAULT_SCOPE_TEMPLATE.stages.map(s => ({
        ...s,
        introduction: s.introduction || '',
        notes: s.notes || '',
        standardDeliverables: s.standardDeliverables.map(d => ({ ...d })),
        optionalDeliverables: s.optionalDeliverables.map(d => ({ ...d })),
      })),
      createdAt: now,
      updatedAt: now,
      confirmedAt: null,
    };
    updateAndPersist(prev => [...prev, tpl]);
    return tpl;
  }, [updateAndPersist]);

  // Duplicate an existing template — deep-copies all content into a new draft
  const duplicateTemplate = useCallback((templateId) => {
    const src = templates.find(t => t.id === templateId);
    if (!src) return null;
    const now = new Date().toISOString();
    const copy = {
      ...src,
      id: `sow-tpl-${++_nextId}`,
      name: `${src.name} (copy)`,
      status: 'draft',
      createdAt: now,
      updatedAt: now,
      confirmedAt: null,
      // Deep-copy template-level fields so edits don't mutate the source
      templateIntroduction: src.templateIntroduction || '',
      interiorDeliverables: (src.interiorDeliverables || []).map(d => ({ ...d })),
      // Deep-copy stages and their deliverable lists
      stages: (src.stages || []).map(s => ({
        ...s,
        standardDeliverables: (s.standardDeliverables || []).map(d => ({ ...d })),
        optionalDeliverables: (s.optionalDeliverables || []).map(d => ({ ...d })),
      })),
    };
    updateAndPersist(prev => [...prev, copy]);
    return copy;
  }, [templates, updateAndPersist]);

  // Update template metadata (name, status, etc.)
  const updateTemplate = useCallback((templateId, updates) => {
    updateAndPersist(prev =>
      prev.map(t =>
        t.id === templateId
          ? { ...t, ...updates, updatedAt: new Date().toISOString() }
          : t
      )
    );
  }, [updateAndPersist]);

  // Update a stage within a template
  const updateTemplateStage = useCallback((templateId, stageKey, stageUpdates) => {
    updateAndPersist(prev =>
      prev.map(t => {
        if (t.id !== templateId) return t;
        return {
          ...t,
          updatedAt: new Date().toISOString(),
          stages: t.stages.map(s =>
            s.key === stageKey ? { ...s, ...stageUpdates } : s
          ),
        };
      })
    );
  }, [updateAndPersist]);

  // Add a custom deliverable to a stage in a template
  const addDeliverable = useCallback((templateId, stageKey, label, type = 'standard') => {
    const id = `tpl-deliv-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const listKey = type === 'optional' ? 'optionalDeliverables' : 'standardDeliverables';
    updateAndPersist(prev =>
      prev.map(t => {
        if (t.id !== templateId) return t;
        return {
          ...t,
          updatedAt: new Date().toISOString(),
          stages: t.stages.map(s => {
            if (s.key !== stageKey) return s;
            return {
              ...s,
              [listKey]: [...s[listKey], { id, label }],
            };
          }),
        };
      })
    );
  }, [updateAndPersist]);

  // Remove a deliverable from a stage in a template
  const removeDeliverable = useCallback((templateId, stageKey, delivId, type = 'standard') => {
    const listKey = type === 'optional' ? 'optionalDeliverables' : 'standardDeliverables';
    updateAndPersist(prev =>
      prev.map(t => {
        if (t.id !== templateId) return t;
        return {
          ...t,
          updatedAt: new Date().toISOString(),
          stages: t.stages.map(s => {
            if (s.key !== stageKey) return s;
            return {
              ...s,
              [listKey]: s[listKey].filter(d => d.id !== delivId),
            };
          }),
        };
      })
    );
  }, [updateAndPersist]);

  // Rename a deliverable
  const renameDeliverable = useCallback((templateId, stageKey, delivId, newLabel, type = 'standard') => {
    const listKey = type === 'optional' ? 'optionalDeliverables' : 'standardDeliverables';
    updateAndPersist(prev =>
      prev.map(t => {
        if (t.id !== templateId) return t;
        return {
          ...t,
          updatedAt: new Date().toISOString(),
          stages: t.stages.map(s => {
            if (s.key !== stageKey) return s;
            return {
              ...s,
              [listKey]: s[listKey].map(d =>
                d.id === delivId ? { ...d, label: newLabel } : d
              ),
            };
          }),
        };
      })
    );
  }, [updateAndPersist]);

  // Reorder stages within a template
  const reorderTemplateStages = useCallback((templateId, orderedStageKeys) => {
    updateAndPersist(prev =>
      prev.map(t => {
        if (t.id !== templateId) return t;
        const stageMap = new Map(t.stages.map(s => [s.key, s]));
        const reordered = orderedStageKeys.map(key => stageMap.get(key)).filter(Boolean);
        return { ...t, updatedAt: new Date().toISOString(), stages: reordered };
      })
    );
  }, [updateAndPersist]);

  // Add a new stage to a template
  const addStage = useCallback((templateId, stageInit = {}) => {
    const key = stageInit.key || `stage-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const newStage = {
      key,
      stageId: stageInit.stageId || null,
      name: stageInit.name || 'New Stage',
      fullName: stageInit.fullName || '',
      description: stageInit.description || '',
      introduction: stageInit.introduction || '',
      standardDeliverables: stageInit.standardDeliverables || [],
      optionalDeliverables: stageInit.optionalDeliverables || [],
      notes: stageInit.notes || '',
    };
    updateAndPersist(prev =>
      prev.map(t =>
        t.id === templateId
          ? { ...t, updatedAt: new Date().toISOString(), stages: [...t.stages, newStage] }
          : t
      )
    );
    return newStage;
  }, [updateAndPersist]);

  // Remove a stage from a template
  const removeStage = useCallback((templateId, stageKey) => {
    updateAndPersist(prev =>
      prev.map(t =>
        t.id === templateId
          ? { ...t, updatedAt: new Date().toISOString(), stages: t.stages.filter(s => s.key !== stageKey) }
          : t
      )
    );
  }, [updateAndPersist]);

  // Move a stage by delta (-1 up, +1 down)
  const moveStage = useCallback((templateId, stageKey, delta) => {
    updateAndPersist(prev =>
      prev.map(t => {
        if (t.id !== templateId) return t;
        const idx = t.stages.findIndex(s => s.key === stageKey);
        if (idx === -1) return t;
        const newIdx = idx + delta;
        if (newIdx < 0 || newIdx >= t.stages.length) return t;
        const stages = [...t.stages];
        const [moved] = stages.splice(idx, 1);
        stages.splice(newIdx, 0, moved);
        return { ...t, updatedAt: new Date().toISOString(), stages };
      })
    );
  }, [updateAndPersist]);

  // Confirm a template — makes it available for use in Contracts & Scope
  const confirmTemplate = useCallback((templateId) => {
    const now = new Date().toISOString();
    updateAndPersist(prev =>
      prev.map(t =>
        t.id === templateId
          ? { ...t, status: 'confirmed', confirmedAt: now, updatedAt: now }
          : t
      )
    );
  }, [updateAndPersist]);

  // Delete a template
  const deleteTemplate = useCallback((templateId) => {
    updateAndPersist(prev => prev.filter(t => t.id !== templateId));
  }, [updateAndPersist]);

  return {
    templates,
    // Pages that display templates must call this on mount — see above.
    ensureLoaded,
    createTemplate,
    duplicateTemplate,
    updateTemplate,
    updateTemplateStage,
    addDeliverable,
    removeDeliverable,
    renameDeliverable,
    reorderTemplateStages,
    addStage,
    removeStage,
    moveStage,
    confirmTemplate,
    deleteTemplate,
  };
}
